import { Component, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { ScreeningBasicInfoComponent } from './steps/basic-info/basic-info.component';
import { ScreeningRequirementsComponent } from './steps/requirements/requirements.component';
import { ScreeningCreatorsComponent } from './steps/creators/creators.component';
import { ScreeningNextStepsComponent } from './steps/next-steps/next-steps.component';
import { CreateSocialScreeningBriefDto, ScreeningBriefResponseDto } from '../../common/general-types/social-screening.dto';
import { CreateScreeningService } from './services/create-screening/create-screening.service';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { SocialScreeningService } from '../../services/social-screening/social-screening.service';
import { AlertService } from '../../atoms/alert/alert.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-screening-brief-create',
  standalone: true,
  imports: [ScreeningBasicInfoComponent, ScreeningRequirementsComponent, ScreeningCreatorsComponent, ScreeningNextStepsComponent],
  templateUrl: './screening-brief-create.component.html',
  styleUrl: './screening-brief-create.component.css',
  animations: [
    trigger('stepSlide', [
      transition(':increment', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':decrement', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class ScreeningBriefCreateComponent {

  briefSteps: { id: number, name: string, status: -1 | 0 | 1 }[] = [
    { id: 0, name: 'Basic Info', status: 0 },
    { id: 1, name: 'Requirements', status: -1 },
    { id: 2, name: 'Creators', status: -1 },
    { id: 3, name: 'What happens next?', status: -1 },
  ];

  currentStep = signal<number>(0);

  showCreatingBriefLoading = signal<boolean>(false);

  /** Services */
  private router = inject(Router);
  private location = inject(Location);
  public createScreeningService = inject(CreateScreeningService);
  private currentUserService = inject(CurrentUserService);
  private socialScreeningService = inject(SocialScreeningService);
  private alertService = inject(AlertService);
  /** Services - END */ 


  nextStep() {
    const lastStepIndex = this.briefSteps.length - 1;

    if (this.currentStep() === lastStepIndex) {
      // User is on the last step
      console.log('Time to submit the brief!');
      this.createScreeningBrief();
      return;
    }

    this.briefSteps[this.currentStep()].status = 1;
    if (this.briefSteps[this.currentStep() + 1]) {
      this.briefSteps[this.currentStep() + 1].status = 0;
    }
    this.currentStep.set(this.currentStep() + 1);
  }


  isLastStep(): boolean {
    return this.currentStep() === this.briefSteps.length - 1;
  }

  previousStep() {
    this.briefSteps[this.currentStep()].status = -1;
    if (this.briefSteps[this.currentStep() - 1]) {
      this.briefSteps[this.currentStep() - 1].status = 0;
    }
    this.currentStep.set(this.currentStep() - 1);
  }

  createScreeningBrief() {
    console.log('createScreeningBrief()');
    this.showCreatingBriefLoading.set(true);

    const newScreeningBrief: CreateSocialScreeningBriefDto = {
      briefTitle: this.createScreeningService.basicInfoModel().briefTitle,
      businessAccountId: this.currentUserService.focussedBrand.id,
      briefUrl: this.createScreeningService.requirementsDocumentUrl(),
      contentRequirements: this.createScreeningService.requirementsHtml(),
      contentDonts: this.createScreeningService.restrictionsHtml(),
      briefText: this.createScreeningService.requirementsHtml(),
      productId: this.createScreeningService.selectedProduct()?.id,
      screenUntil: this.getScreeningUnit() ,
      creatorListId: this.createScreeningService.selectedList()?.id,
    }

    console.log('New screening brief: ', newScreeningBrief);

    this.socialScreeningService.createSocialScreeningBrief(newScreeningBrief).subscribe({
      next: async (response: any) => {
        console.log('Screening brief created: ', response);
        this.showCreatingBriefLoading.set(false);
        const alertResponse = await this.alertService.alert({
          title: 'Screening brief created successfully!',
          message: 'You can now view your screening brief in the screening briefs page.',
          icon: 'success',
          confirmText: 'View Screening Brief',
          cancelText: 'Close',
          type: 'confirm',
        });
        console.log('alertResponse: ', alertResponse);
        if (alertResponse) {
          this.router.navigate(['/manage-brief-screen', response.id]);
        } else {
          this.location.back();
        }
      },
      error: (error) => {
        console.error('Error creating screening brief: ', error);
        this.showCreatingBriefLoading.set(false);
      }
    });
  }

  getScreeningUnit() {
    if (this.createScreeningService.screenContentUntil()) {
      return this.createScreeningService.screenContentUntil()!;
    } else {
      return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    }
  }


  getBriefStepClass(step: { id: number, name: string, status: -1 | 0 | 1 }) {
    switch (step.status) {
      case 0:
        return 'border-primary dark:bg-primary/20 bg-primary/10 text-accent';
      case -1:
        return 'border-zinc-500 dark:bg-zinc-900/30 bg-zinc-50 dark:text-zinc-300 text-zinc-500';
      case 1:
        return 'border-green-500 dark:bg-green-900/30 bg-green-50 dark:text-green-300 text-green-500';
    }
  }

  getStepCircleClass(step: { id: number, name: string, status: -1 | 0 | 1 }): string {
    switch (step.status) {
      case 1:
        return 'bg-primary border-primary text-white';
      case 0:
        return 'bg-white dark:bg-gray-800 border-primary text-accent';
      case -1:
        return 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400';
    }
  }

  getStepLabelClass(step: { id: number, name: string, status: -1 | 0 | 1 }): string {
    switch (step.status) {
      case 1:
        return 'text-accent';
      case 0:
        return 'text-accent';
      case -1:
        return 'text-gray-500 dark:text-gray-400';
    }
  }

  getConnectorClass(currentStep: { id: number, name: string, status: -1 | 0 | 1 }, nextStep: { id: number, name: string, status: -1 | 0 | 1 } | undefined): string {
    if (currentStep.status === 1) {
      return 'bg-primary dark:bg-primary';
    }
    return 'bg-gray-300 dark:bg-gray-600';
  }

  backToBriefs() {
    this.location.back();
  }
}
